import { useEffect, useState } from 'react';
/**
 * Mobile experience mode — single source of truth for which mobile
 * features are enabled.
 *
 *  'full'                 — original mobile-editor behaviour. Used
 *                           pre-launch and post-companion-app launch
 *                           (when we flip back to "yes, edit on
 *                           mobile too" alongside the native app).
 *
 *  'view-comment-share'   — default at launch. Reading + validation
 *                           + comment threads + share drawer all work
 *                           fully. Block creation / editing / delete /
 *                           convert / reorder are hidden. Tapping a
 *                           block opens the read-only BlockDetailDrawer
 *                           instead of the editor.
 *
 *  'locked'               — heavy-handed: every mobile studio route
 *                           bounces to a "use desktop" landing. Not
 *                           used at launch; reserved if we ever decide
 *                           to fully gate mobile (e.g., for an
 *                           enterprise tier).
 *
 * Read from import.meta.env.VITE_MOBILE_MODE. Unrecognised values fall
 * back to 'view-comment-share' so a typo in env config never
 * accidentally re-enables full editing.
 *
 * See lilia-docs/launch-readiness/2026-05-18-mobile-companion-postponed.md
 * for the why.
 */

export type MobileMode = 'full' | 'view-comment-share' | 'locked';

const VALID_MODES: ReadonlySet<MobileMode> = new Set([
  'full',
  'view-comment-share',
  'locked',
]);

/**
 * Resolved once at module load. Mobile mode is a build-time decision
 * (the relevant code is dead-stripped from bundles where appropriate),
 * not a runtime toggle.
 */
const RESOLVED_MODE: MobileMode = (() => {
  const raw = import.meta.env.VITE_MOBILE_MODE;
  if (typeof raw === 'string' && VALID_MODES.has(raw as MobileMode)) {
    return raw as MobileMode;
  }
  // Default at launch. When the companion app ships, set this env var
  // to 'full' in the desktop-edition build and ship a 'view-comment-
  // share' variant for the residual mobile-web audience.
  return 'view-comment-share';
})();

/**
 * Stable hook — same value for the lifetime of the SPA session. No
 * subscription needed; consumers just call this at render and branch.
 */
export function useMobileMode(): MobileMode {
  return RESOLVED_MODE;
}

/**
 * Helper for the most common branch — "is mobile editing currently
 * allowed?" Returns true only when mode === 'full'.
 */
export function useMobileEditingEnabled(): boolean {
  return useMobileMode() === 'full';
}

/**
 * Helper for the read-mode shell — "should I render the new
 * bottom-bar / banner / block-detail-drawer chrome?"
 */
export function useMobileReadMode(): boolean {
  return useMobileMode() === 'view-comment-share';
}

/**
 * The mobile reader's screens (Olivia, design-handoffs/2026-10-10-mobile-reader):
 * under 768 px wide, or a touch screen under 500 px tall (a phone turned sideways).
 * Landscape tablets (1138 × 712 and the like) stay in the full editor.
 */
export const PHONE_QUERY = "(max-width: 767px), (pointer: coarse) and (max-height: 500px)";

/** Whether the screen is a phone now (the mobile reader); follows rotation and resizes. */
export function usePhoneViewport(): boolean {
  const query = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;
  const [phone, setPhone] = useState(query);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(mql.matches);
    on();
    mql.addEventListener?.("change", on);
    return () => mql.removeEventListener?.("change", on);
  }, []);
  return phone;
}

/**
 * The document can't be typed into on a phone (user, 10 Oct 2026: "as soon as I
 * touch the document content, it brings the keyboard"): it is read, and changed
 * through Ask Lilia. Only `VITE_MOBILE_MODE=full` brings phone editing back.
 */
export function usePhoneReadOnly(): boolean {
  const phone = usePhoneViewport();
  return phone && !useMobileEditingEnabled();
}

/** The phone gets the mobile reader instead of the editor's workspace. */
export const useMobileReader = usePhoneViewport;
